'use client';

import { useEffect, useRef, useState } from 'react';
import { useLoadedJpgSource } from '@/components/tools/jpg-tool-shared';
import { encodeJpegBlobFromCrossImage } from '@/lib/browser-jpg-tools';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import { formatFileSize, getBaseName } from '@/lib/browser-image-tools';

export default function BrowserCmykToSrgbJpgConverter() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { source, loadSource, clearSource, sourcePreviewUrl } = useLoadedJpgSource({
    allowJpeg: true,
    allowHeic: false,
  });
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultPreviewUrl, setResultPreviewUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      [downloadUrl, resultPreviewUrl].forEach((url) => {
        if (url) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [downloadUrl, resultPreviewUrl]);

  function resetOutputState() {
    [downloadUrl, resultPreviewUrl].forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    setDownloadUrl(null);
    setDownloadName(null);
    setResultPreviewUrl(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      clearSource();
      return;
    }

    try {
      await loadSource(file);
    } catch (error) {
      clearSource();
      setErrorMessage(error instanceof Error ? error.message : 'The JPG file could not be loaded.');
    }
  }

  async function runConversion() {
    if (!source) {
      setErrorMessage('Choose a JPG file first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const credits = await consumeToolUsage(
        'cmyk-to-srgb-jpg-converter',
        'Credits are not available for another color-profile conversion right now.',
      );
      const blob = await encodeJpegBlobFromCrossImage(source.image, {
        quality: 0.9,
        progressive: false,
      });
      const nextUrl = URL.createObjectURL(blob);

      resetOutputState();
      setDownloadUrl(nextUrl);
      setResultPreviewUrl(nextUrl);
      setDownloadName(`${getBaseName(source.file.name)}-srgb.jpg`);
      setResultSize(blob.size);
      setCreditMessage(formatToolCreditSuccess('Color profile normalization', credits));
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The JPG file could not be normalized.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  const statusLabel = source?.jpegStructure?.isCmykLike
    ? 'CMYK / YCCK detected'
    : 'RGB JPG or unknown';

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Normalize a CMYK-style JPG to sRGB</h2>
          <p>
            Rebuild CMYK, YCCK, or Adobe-marker JPGs into a standard sRGB JPG that is safer for web
            uploads and browsers.
          </p>
        </div>
        <span className="tool-live-badge">Color</span>
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{source ? source.file.name : 'Choose one JPG or drop it here'}</strong>
              <span>
                {source
                  ? `${formatFileSize(source.file.size)} • ${source.width} × ${source.height}`
                  : 'Best for CMYK or odd-looking JPEG uploads'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/jpeg,.jpg,.jpeg"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!source || isProcessing}
              onClick={() => void runConversion()}
              type="button"
            >
              {isProcessing ? 'Normalizing…' : 'Normalize JPG'}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download JPG
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Profile status</h3>
          <div className="tool-live-stats">
            <div>
              <strong>Detected</strong>
              <span>{source ? statusLabel : 'Add a JPG'}</span>
            </div>
            <div>
              <strong>Output</strong>
              <span>sRGB JPG</span>
            </div>
            {resultSize ? (
              <div>
                <strong>Result size</strong>
                <span>{formatFileSize(resultSize)}</span>
              </div>
            ) : null}
          </div>
          <div className="tool-live-panel-note">
            <strong>How this helps</strong>
            <span>
              Even if the source is already a normal RGB JPG, this rebuild still gives you a clean
              web-friendly export.
            </span>
          </div>
        </aside>
      </div>

      {source ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Before and after</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame">
              <img
                alt="Original JPG preview"
                src={source.nativePreviewUrl ?? source.decodedPreviewUrl}
              />
            </div>
            <div className="tool-live-preview-frame">
              {resultPreviewUrl ? (
                <img alt="Normalized JPG preview" src={resultPreviewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Normalized JPG</strong>
                  <span>Run the normalization to preview the sRGB export.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </article>
  );
}
